import { useEffect, useMemo, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { Bot, Copy, Inbox, Play, Repeat, Shuffle, Trash2, User, UserMinus } from "lucide-react"
import { InfoTooltip } from "@/components/ui/info-tooltip"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, MultiSelect } from "@/components/ui/select"
import type { SelectOption } from "@/components/ui/select"
import { CreatableSelect } from "@/components/ui/creatable-select"
import { cn } from "@/lib/cn"
import { PredicateBuilder } from "@/components/domain/policy-predicate/predicate-builder"
import { TestRuleModal } from "@/components/domain/policy-predicate/test-rule-modal"
import type {
  AgentPoolOption,
  AssigneeStrategy,
  CustomFieldCatalog,
  EscalationPolicy,
  PolicyAgentOption,
  PolicyPriority,
  PolicyReasonOption,
  PolicyTopicOption,
  PolicyUserOption,
  TicketQueueOption,
} from "@/types/escalation_policy"

// Right-side full editor panel for the Auto Triage list+editor layout.
// Holds a local-state buffer of the policy fields. Cancel reverts; Save
// PATCHes via `onSave`. Save button is disabled until the local buffer
// differs from the prop. After a successful Inertia reload the parent
// re-passes the same `policy` (or a refreshed copy) and an effect resyncs
// the buffer.
//
// Layout follows the routing-rules mockup:
//   1. Header strip — title + (Active toggle, Duplicate, Save changes)
//   2. Rule name input
//   3. Numbered section "1. Match basics"        — reasons / topics / repeat
//   4. Numbered section "2. Match custom fields" — predicate builder
//   5. Numbered section "3. Routing action"      — action + pool + priority + SLA
//   6. Test-rule sticky strip
//   7. Footer with delete-rule link

const STRATEGY_BUTTONS: { value: AssigneeStrategy; label: string; Icon: typeof User }[] = [
  { value: "unassigned",            label: "Leave unassigned", Icon: UserMinus },
  { value: "specific_user",         label: "Assign user",      Icon: User },
  { value: "random_from_pool",      label: "Random from pool", Icon: Shuffle },
  { value: "round_robin_from_pool", label: "Round robin",      Icon: Repeat },
  { value: "send_to_queue",         label: "Send to queue",    Icon: Inbox },
  { value: "route_to_agent",        label: "Reroute to AI agent", Icon: Bot },
]

const PRIORITY_OPTIONS: SelectOption<string>[] = [
  { value: "",        label: "Leave alone" },
  { value: "urgent",  label: "Urgent" },
  { value: "high",    label: "High" },
  { value: "normal",  label: "Normal" },
  { value: "low",     label: "Low" },
]

type LocalBuffer = {
  name: string
  active: boolean
  match_reason_keys_include: string[]
  match_reason_keys_exclude: string[]
  match_topic_keys_include: string[]
  match_topic_keys_exclude: string[]
  match_custom_fields: Record<string, unknown>
  match_min_recent_escalations: number | null
  match_recent_window_days: number | null
  assignee_strategy: AssigneeStrategy
  assignee_user_id: number | null
  assignee_agent_pool_id: number | null
  assignee_ticket_queue_id: number | null
  prefer_topic_affinity: boolean
  reroute_agent_id: number | null
  priority_override: PolicyPriority | null
  sla_first_response_minutes: number | null
  sla_idle_response_minutes: number | null
  sla_resolution_minutes: number | null
}

function bufferFrom(p: EscalationPolicy): LocalBuffer {
  return {
    name: p.name,
    active: p.active,
    match_reason_keys_include:  [...p.match_reason_keys_include],
    match_reason_keys_exclude:  [...p.match_reason_keys_exclude],
    match_topic_keys_include:   [...p.match_topic_keys_include],
    match_topic_keys_exclude:   [...p.match_topic_keys_exclude],
    match_custom_fields:        cloneJson(p.match_custom_fields),
    match_min_recent_escalations: p.match_min_recent_escalations,
    match_recent_window_days:     p.match_recent_window_days,
    assignee_strategy:    p.assignee_strategy,
    assignee_user_id:     p.assignee_user_id,
    assignee_agent_pool_id: p.assignee_agent_pool_id,
    assignee_ticket_queue_id: p.assignee_ticket_queue_id,
    prefer_topic_affinity: p.prefer_topic_affinity,
    reroute_agent_id:      p.reroute_agent_id,
    priority_override:     p.priority_override,
    sla_first_response_minutes: p.sla_first_response_minutes,
    sla_idle_response_minutes:  p.sla_idle_response_minutes,
    sla_resolution_minutes:     p.sla_resolution_minutes,
  }
}

function cloneJson(v: Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!v) return {}
  try { return JSON.parse(JSON.stringify(v)) as Record<string, unknown> }
  catch { return {} }
}

function bufferEquals(a: LocalBuffer, b: LocalBuffer): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

type Props = {
  policy: EscalationPolicy
  // Full ordered policy list for the active set. The Test rules modal
  // renders the same list with per-rule pass/fail + winner, so it
  // needs the same ordering the FE already shows on the left.
  policies: EscalationPolicy[]
  users: PolicyUserOption[]
  agents: PolicyAgentOption[]
  reasonOptions: PolicyReasonOption[]
  topicOptions: PolicyTopicOption[]
  agentPools: AgentPoolOption[]
  ticketQueues: TicketQueueOption[]
  customFieldCatalog: CustomFieldCatalog
  editable: boolean
  ruleNumber: number
  onSave: (id: number, body: Partial<EscalationPolicy>) => void
  onDelete: (id: number) => void
  onToggleActive: (id: number, active: boolean) => void
}

export function PolicyEditorPanel({
  policy,
  policies,
  users,
  agents,
  reasonOptions,
  topicOptions,
  agentPools,
  ticketQueues,
  customFieldCatalog,
  editable,
  ruleNumber,
  onSave,
  onDelete,
  onToggleActive,
}: Props) {
  const [testOpen, setTestOpen] = useState(false)
  const original = useMemo(() => bufferFrom(policy), [policy])
  const [buf, setBuf] = useState<LocalBuffer>(original)

  // Re-sync local buffer whenever the policy prop changes (Inertia reload).
  useEffect(() => {
    setBuf(bufferFrom(policy))
  }, [policy])

  const dirty = !bufferEquals(buf, original)
  const removed = policy.diffStatus === "removed"
  const disabled = !editable || removed

  const isCatchAll =
    Object.keys(buf.match_custom_fields).length === 0 &&
    buf.match_reason_keys_include.length === 0 &&
    buf.match_reason_keys_exclude.length === 0 &&
    buf.match_topic_keys_include.length === 0 &&
    buf.match_topic_keys_exclude.length === 0 &&
    !buf.match_min_recent_escalations

  const reasonSelectOptions: SelectOption<string>[] = reasonOptions.map((r) => ({
    value: r.key,
    label: `${r.agentKey} · ${r.name}`,
  }))
  const topicSelectOptions: SelectOption<string>[] = topicOptions.map((t) => ({
    value: t.key,
    label: t.name,
  }))
  const userSelectOptions: SelectOption<number>[] = users.map((u) => ({
    value: u.id,
    label: `${u.name} (${u.email})`,
  }))
  const agentSelectOptions: SelectOption<number>[] = agents.map((a) => ({
    value: a.id,
    label: a.name,
  }))
  const poolSelectOptions: SelectOption<number>[] = agentPools.map((p) => ({
    value: p.id,
    label: `${p.name} (${p.memberCount} member${p.memberCount === 1 ? "" : "s"})`,
  }))
  const queueSelectOptions: SelectOption<number>[] = ticketQueues.map((q) => ({
    value: q.id,
    label: q.name,
  }))

  const update = <K extends keyof LocalBuffer>(key: K, value: LocalBuffer[K]) =>
    setBuf((prev) => ({ ...prev, [key]: value }))

  const cancel = () => setBuf(original)

  const submit = () => {
    onSave(policy.id, {
      name: buf.name,
      active: buf.active,
      match_reason_keys_include:  buf.match_reason_keys_include,
      match_reason_keys_exclude:  buf.match_reason_keys_exclude,
      match_topic_keys_include:   buf.match_topic_keys_include,
      match_topic_keys_exclude:   buf.match_topic_keys_exclude,
      match_custom_fields:        buf.match_custom_fields,
      match_min_recent_escalations: buf.match_min_recent_escalations,
      match_recent_window_days:     buf.match_recent_window_days,
      assignee_strategy:    buf.assignee_strategy,
      assignee_user_id:     buf.assignee_strategy === "specific_user" ? buf.assignee_user_id : null,
      assignee_agent_pool_id:
        buf.assignee_strategy === "random_from_pool" || buf.assignee_strategy === "round_robin_from_pool"
          ? buf.assignee_agent_pool_id : null,
      assignee_ticket_queue_id:
        buf.assignee_strategy === "send_to_queue" ? buf.assignee_ticket_queue_id : null,
      prefer_topic_affinity: buf.prefer_topic_affinity,
      reroute_agent_id:      buf.assignee_strategy === "route_to_agent" ? buf.reroute_agent_id : null,
      priority_override:     buf.priority_override,
      sla_first_response_minutes: buf.sla_first_response_minutes,
      sla_idle_response_minutes:  buf.sla_idle_response_minutes,
      sla_resolution_minutes:     buf.sla_resolution_minutes,
    })
  }

  const duplicate = () => {
    router.post(`/escalation_policies/${policy.id}/duplicate`, {}, { preserveScroll: true })
  }

  // Soft warning, not a block — see plan: idle SLA without first-response
  // SLA means the initial wait window is untracked, which is almost always
  // a config mistake but operators may have a reason for it. Yellow note,
  // no save-disable.
  const slaIdleWithoutFirstResponse =
    buf.sla_idle_response_minutes != null && buf.sla_first_response_minutes == null

  // Inline-create handler for both pickers below. Posts to the resource
  // controller, returns the newly-minted option so CreatableSelect can
  // call onChange with it. Failures bubble as console errors — the
  // operator's input stays in the field so they can retry.
  async function createTicketQueue(name: string): Promise<SelectOption<number> | null> {
    const token = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content
    const res = await fetch("/ticket_queues", {
      method:      "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": token ?? "",
        "Content-Type": "application/json",
        "Accept":       "application/json",
      },
      body: JSON.stringify({ ticket_queue: { name } }),
    })
    if (!res.ok) return null
    const data = await res.json() as { id: number; name: string }
    return { value: data.id, label: data.name }
  }

  async function createAgentPool(name: string): Promise<SelectOption<number> | null> {
    const token = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content
    const res = await fetch("/agent_pools", {
      method:      "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": token ?? "",
        "Content-Type": "application/json",
        "Accept":       "application/json",
      },
      body: JSON.stringify({ agent_pool: { name } }),
    })
    if (!res.ok) return null
    const data = await res.json() as { id: number; name: string; memberCount?: number }
    const count = data.memberCount ?? 0
    return { value: data.id, label: `${data.name} (${count} member${count === 1 ? "" : "s"})` }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header — title left, controls (Active / Duplicate / Save) right */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-base font-semibold text-app-text">Edit routing rule</span>
            <span className="text-xs text-app-muted">Rule #{ruleNumber}</span>
            {removed && <span className="text-xs italic text-app-muted">(removed)</span>}
            {isCatchAll && (
              <span className="text-[11px] italic text-status-success">
                catch-all
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-app-muted">
            <span>Active</span>
            <ToggleSwitch
              checked={buf.active}
              disabled={disabled}
              onChange={(next) => {
                update("active", next)
                onToggleActive(policy.id, next)
              }}
            />
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTestOpen(true)}
            title="Test all rules against an appuser + conversation pair"
          >
            <span className="inline-flex items-center gap-1.5">
              <Play size={12} />
              Test
            </span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={duplicate}
            disabled={disabled}
            title="Copy this rule into a new one at the bottom of the draft"
          >
            <span className="inline-flex items-center gap-1.5">
              <Copy size={12} />
              Duplicate
            </span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (confirm(`Delete "${policy.name}"?`)) onDelete(policy.id)
            }}
            disabled={disabled}
            title="Delete this rule"
          >
            <span className="inline-flex items-center gap-1.5 text-status-error">
              <Trash2 size={12} />
              Delete
            </span>
          </Button>
          <Button variant="red" size="sm" onClick={submit} disabled={!dirty || disabled}>
            Save changes
          </Button>
        </div>
      </div>

      {/* Body — scrollable */}
      <div className="flex-1 space-y-6 overflow-y-auto px-5 py-4">
        {/* Rule name */}
        <div>
          <Label>Rule name</Label>
          <Input
            value={buf.name}
            disabled={disabled}
            onChange={(e) => update("name", e.target.value)}
            className="w-full"
          />
        </div>

        {/* 1. Match basics — reasons / topics / repeat escalations */}
        <NumberedSection
          n={1}
          title="Match basics"
          help="Structured filters from the convo's classifier output and recent-escalation history. All specified conditions must match."
        >
          {/* Reasons */}
          <PairedRow label="Escalation reasons" excludeLabel="Exclude">
            <MultiPicker
              values={buf.match_reason_keys_include}
              options={reasonSelectOptions}
              onChange={(v) => update("match_reason_keys_include", v)}
              disabled={disabled}
              placeholder="+ Add reasons"
            />
            <MultiPicker
              values={buf.match_reason_keys_exclude}
              options={reasonSelectOptions}
              onChange={(v) => update("match_reason_keys_exclude", v)}
              disabled={disabled}
              placeholder="+ Add reasons"
            />
          </PairedRow>

          {/* Topics */}
          <PairedRow label="Topics" excludeLabel="Exclude">
            <MultiPicker
              values={buf.match_topic_keys_include}
              options={topicSelectOptions}
              onChange={(v) => update("match_topic_keys_include", v)}
              disabled={disabled}
              placeholder="+ Add topics"
            />
            <MultiPicker
              values={buf.match_topic_keys_exclude}
              options={topicSelectOptions}
              onChange={(v) => update("match_topic_keys_exclude", v)}
              disabled={disabled}
              placeholder="+ Add topics"
            />
          </PairedRow>

          {/* Repeat escalations */}
          <div className="space-y-1.5 pt-1">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-app-muted">
              <span>Repeat escalations</span>
              <InfoTooltip side="right">
                Fire only when the appuser has escalated repeatedly in the recent window.
              </InfoTooltip>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-app-muted">At least</span>
              <Input
                type="number"
                min={0}
                disabled={disabled}
                value={buf.match_min_recent_escalations ?? ""}
                onChange={(e) =>
                  update(
                    "match_min_recent_escalations",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                placeholder="N"
                className="w-20"
              />
              <span className="text-app-muted">escalations in last</span>
              <Input
                type="number"
                min={1}
                disabled={disabled}
                value={buf.match_recent_window_days ?? ""}
                onChange={(e) =>
                  update(
                    "match_recent_window_days",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                placeholder="7"
                className="w-20"
              />
              <span className="text-app-muted">days. Leave both blank to disable.</span>
            </div>
          </div>
        </NumberedSection>

        {/* 2. Match custom fields — predicate builder */}
        <NumberedSection
          n={2}
          title="Match custom fields"
          subtitle="Add appuser or conversation field predicates. Use groups to mix AND / OR logic."
          help={
            <div className="space-y-2">
              <div>
                Match the appuser's custom fields and the conversation's custom
                fields against typed conditions. The predicate is evaluated at
                triage time; the first policy whose conditions all hold wins.
              </div>
              <div className="text-app-muted">
                Use <strong>+ Match on source_state</strong> for the most common
                rule (gating by partner-side classification), or build a richer
                tree with AND / OR groups.
              </div>
            </div>
          }
        >
          <PredicateBuilder
            value={buf.match_custom_fields}
            catalog={customFieldCatalog}
            onChange={(next) => update("match_custom_fields", next)}
            disabled={disabled}
          />
        </NumberedSection>

        {/* 3. Routing action — strategy + assignee + priority + SLA */}
        <NumberedSection
          n={3}
          title="Routing action"
          help="Which teammate or agent gets this escalation, plus the priority and SLA targets stamped onto the ticket at triage."
        >
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
            {STRATEGY_BUTTONS.map(({ value, label, Icon }) => {
              const isOn = buf.assignee_strategy === value
              return (
                <button
                  key={value}
                  type="button"
                  disabled={disabled}
                  onClick={() => update("assignee_strategy", value)}
                  className={cn(
                    "inline-flex flex-col items-center justify-center gap-1 rounded-control px-2 py-3 text-[11px] font-medium transition",
                    isOn
                      ? "bg-brand-red text-white shadow-redGlow"
                      : "border border-border bg-app-panel2 text-app-text hover:bg-white/5",
                    disabled && "opacity-50 cursor-not-allowed",
                  )}
                >
                  <Icon size={14} />
                  <span className="truncate text-center leading-tight">{label}</span>
                </button>
              )
            })}
          </div>

          <div className="pt-2">
            {buf.assignee_strategy === "unassigned" && (
              <div className="text-[11px] text-app-muted">
                The escalation will remain unassigned.
              </div>
            )}

            {buf.assignee_strategy === "specific_user" && (
              <div className="space-y-1.5">
                <Label>Assignee</Label>
                <div className="max-w-md">
                  <Select<number>
                    value={userSelectOptions.find((o) => o.value === buf.assignee_user_id) ?? null}
                    onChange={(o) => update("assignee_user_id", o ? o.value : null)}
                    options={userSelectOptions}
                    isDisabled={disabled}
                    placeholder="Pick a teammate…"
                  />
                </div>
              </div>
            )}

            {(buf.assignee_strategy === "random_from_pool" ||
              buf.assignee_strategy === "round_robin_from_pool") && (
              <div className="space-y-2">
                <Label>Pool</Label>
                <div className="max-w-md">
                  <CreatableSelect<number>
                    value={poolSelectOptions.find((o) => o.value === buf.assignee_agent_pool_id) ?? null}
                    onChange={(o) => update("assignee_agent_pool_id", o ? o.value : null)}
                    options={poolSelectOptions}
                    onCreate={createAgentPool}
                    isDisabled={disabled}
                    placeholder="Pick a pool or type a new name to create one…"
                    formatCreateLabel={(input) => `Create pool "${input}"`}
                  />
                </div>
                <div className="text-[11px]">
                  <Link href="/agent_pools" className="text-brand-red hover:underline">
                    Manage support pools →
                  </Link>
                </div>
                <label className="flex cursor-pointer items-start gap-2 text-xs text-app-text">
                  <input
                    type="checkbox"
                    checked={buf.prefer_topic_affinity}
                    disabled={disabled}
                    onChange={(e) => update("prefer_topic_affinity", e.target.checked)}
                    className="mt-0.5 accent-brand-red"
                  />
                  <span>
                    <span className="inline-flex items-center gap-1.5 font-medium">
                      Prefer topic affinity
                      <InfoTooltip side="right">
                        <div className="space-y-2">
                          <div className="font-semibold text-app-text">How specialists are picked</div>
                          <p>
                            When this is on, the rule prefers teammates who have a track record on
                            the convo's topics. If a specialist exists, the ticket goes to one of
                            them. If none, it falls back to the whole pool.
                          </p>
                          <div className="font-semibold text-app-text">How affinity is computed</div>
                          <ul className="list-disc space-y-1 pl-4">
                            <li>We look at the last <strong>30 days</strong> of escalated tickets.</li>
                            <li>For each teammate and each topic, we score the <strong>positive close rate</strong>: thumbs-up customer ratings divided by every ticket they handled on that topic.</li>
                            <li>If a teammate doesn't close their tickets, the unanswered ones still count in the denominator — they get penalized.</li>
                            <li>A teammate needs at least <strong>5 tickets</strong> handled on a topic to be eligible.</li>
                            <li>The <strong>top 50%</strong> on each topic earn the affinity.</li>
                            <li>Recomputed every night automatically.</li>
                            <li>Manual topic preferences set on a teammate's profile always count too.</li>
                          </ul>
                          <div className="font-semibold text-app-text">Overload escape</div>
                          <p>
                            If every specialist is already holding <strong>25 or more</strong> open
                            escalations, the rule falls back to the full pool so the ticket doesn't
                            pile on a saturated person.
                          </p>
                        </div>
                      </InfoTooltip>
                    </span>
                    <span className="block text-app-muted">
                      Falls back to pool otherwise.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {buf.assignee_strategy === "route_to_agent" && (
              <div className="max-w-md space-y-1.5">
                <Label>AI agent</Label>
                <Select<number>
                  value={agentSelectOptions.find((o) => o.value === buf.reroute_agent_id) ?? null}
                  onChange={(o) => update("reroute_agent_id", o ? o.value : null)}
                  options={agentSelectOptions}
                  isDisabled={disabled}
                  placeholder="Pick an AI agent…"
                />
                <div className="text-[11px] text-app-muted">
                  Hands the convo off to another AI agent (e.g. a billing or technical specialist) — no human assignee.
                </div>
              </div>
            )}

            {buf.assignee_strategy === "send_to_queue" && (
              <div className="max-w-md space-y-1.5">
                <Label>Queue</Label>
                <CreatableSelect<number>
                  value={queueSelectOptions.find((o) => o.value === buf.assignee_ticket_queue_id) ?? null}
                  onChange={(o) => update("assignee_ticket_queue_id", o ? o.value : null)}
                  options={queueSelectOptions}
                  onCreate={createTicketQueue}
                  isDisabled={disabled}
                  placeholder="Pick a queue or type a new name to create one…"
                  formatCreateLabel={(input) => `Create queue "${input}"`}
                />
                <div className="text-[11px] text-app-muted">
                  Drops the ticket into this queue's lane in the Tickets sidebar. Whoever's on rotation for the queue picks it up — no specific assignee.
                </div>
              </div>
            )}
          </div>

          {/* Priority + SLA — laid out as a single grid like the mockup.
              Both columns share the same vertical rhythm (uppercase
              header → text-[11px] sub-label → control) so the Select
              and the SLA inputs sit on the same baseline. */}
          <div className="grid gap-4 pt-3 md:grid-cols-[minmax(0,220px)_minmax(0,1fr)]">
            <div className="space-y-1.5">
              <Label help="Force-set the escalation's priority regardless of the agent's default.">
                Priority override
              </Label>
              <div className="space-y-1">
                <div className="text-[11px] text-app-subtle">Level</div>
                <Select<string>
                  value={PRIORITY_OPTIONS.find((o) => o.value === (buf.priority_override ?? "")) ?? null}
                  onChange={(o) =>
                    update(
                      "priority_override",
                      !o || o.value === "" ? null : (o.value as PolicyPriority),
                    )
                  }
                  options={PRIORITY_OPTIONS}
                  isDisabled={disabled}
                  isSearchable={false}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-app-muted">
                <span>Human SLA targets</span>
                <InfoTooltip side="right">
                  <div className="space-y-2">
                    <div>
                      <strong>SLA</strong> = Service Level Agreement. Time targets your team
                      commits to once a ticket is escalated to a human.
                    </div>
                    <ul className="space-y-1 pl-3 list-disc">
                      <li><strong>First-response</strong> — escalation → first operator reply.</li>
                      <li><strong>Idle response</strong> — rolling clock, resets on each customer
                        message; clears when an operator replies.</li>
                      <li><strong>Resolution</strong> — escalation → ticket closed.</li>
                    </ul>
                    <div className="text-app-muted">
                      Values are in minutes. Leave blank to inherit the company default.
                    </div>
                  </div>
                </InfoTooltip>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <div className="space-y-1">
                  <div className="text-[11px] text-app-subtle">First response (min)</div>
                  <Input
                    type="number"
                    min={1}
                    disabled={disabled}
                    value={buf.sla_first_response_minutes ?? ""}
                    onChange={(e) =>
                      update(
                        "sla_first_response_minutes",
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    placeholder="Inherit"
                  />
                </div>
                <div className="space-y-1">
                  <div className="text-[11px] text-app-subtle">Idle response (min)</div>
                  <Input
                    type="number"
                    min={1}
                    disabled={disabled}
                    value={buf.sla_idle_response_minutes ?? ""}
                    onChange={(e) =>
                      update(
                        "sla_idle_response_minutes",
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    placeholder="Inherit"
                  />
                </div>
                <div className="space-y-1">
                  <div className="text-[11px] text-app-subtle">Resolution (min)</div>
                  <Input
                    type="number"
                    min={1}
                    disabled={disabled}
                    value={buf.sla_resolution_minutes ?? ""}
                    onChange={(e) =>
                      update(
                        "sla_resolution_minutes",
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    placeholder="Inherit"
                  />
                </div>
              </div>
              {slaIdleWithoutFirstResponse && (
                <div className="rounded-control border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-[11px] text-status-warning">
                  Idle SLA without a first-response SLA means the initial wait is untracked.
                </div>
              )}
            </div>
          </div>
        </NumberedSection>
      </div>

      {/* Footer — Cancel only when dirty. Delete moved into the header
          alongside Save changes. */}
      {dirty && (
        <div className="flex items-center justify-end border-t border-border px-5 py-2.5">
          <Button variant="ghost" size="sm" onClick={cancel}>
            Cancel
          </Button>
        </div>
      )}

      {/* Test rules modal — opened from the header Test button. */}
      {testOpen && (
        <TestRuleModal
          policies={policies}
          selectedPolicyId={policy.id}
          onClose={() => setTestOpen(false)}
        />
      )}
    </div>
  )
}

// -- subcomponents --------------------------------------------------------

function Label({ children, help }: { children: React.ReactNode; help?: string }) {
  return (
    <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wide text-app-muted">
      <span>{children}</span>
      {help && <InfoTooltip side="right">{help}</InfoTooltip>}
    </div>
  )
}

// Numbered section with a red rounded number badge to mirror the routing-
// rules mockup. Optional subtitle renders inline next to the title.
function NumberedSection({
  n, title, subtitle, help, children,
}: {
  n: number
  title: string
  subtitle?: string
  help?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="space-y-3 rounded-card border border-border bg-app-panel2/40 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-red/15 text-[12px] font-semibold text-brand-red ring-1 ring-brand-red/30">
          {n}
        </span>
        <span className="text-sm font-semibold text-app-text">{title}</span>
        {help && <InfoTooltip side="right">{help}</InfoTooltip>}
        {subtitle && (
          <span className="text-[11px] text-app-muted">{subtitle}</span>
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  )
}

// A row that mirrors the design's "Field — Include … Exclude …" layout.
// Children render inside two equal columns under labelled headers.
function PairedRow({
  label, excludeLabel, children,
}: {
  label: string
  excludeLabel: string
  children: [React.ReactNode, React.ReactNode]
}) {
  const [include, exclude] = children
  return (
    <div className="grid gap-x-3 gap-y-1 md:grid-cols-[120px_minmax(0,1fr)_120px_minmax(0,1fr)] md:items-center">
      <div className="text-xs text-app-muted md:pr-1">{label}</div>
      <div>{include}</div>
      <div className="text-xs text-app-muted md:pr-1 md:pl-2 mt-1.5 md:mt-0">{excludeLabel}</div>
      <div>{exclude}</div>
    </div>
  )
}

// Single-line picker for typed-key dimensions (escalation reasons,
// conversation topics). Shows selected values as chips + lets the
// operator search/add inline — no separate "+ Add" row.
function MultiPicker({
  values, options, onChange, disabled, placeholder,
}: {
  values: string[]
  options: SelectOption<string>[]
  onChange: (next: string[]) => void
  disabled?: boolean
  placeholder?: string
}) {
  const selected: SelectOption<string>[] = values
    .map((v) => options.find((o) => o.value === v))
    .filter((o): o is SelectOption<string> => Boolean(o))

  return (
    <MultiSelect<string>
      value={selected}
      options={options}
      onChange={(next) => {
        const arr = (next ?? []) as readonly SelectOption<string>[]
        onChange(arr.map((o) => o.value))
      }}
      isDisabled={disabled}
      placeholder={placeholder ?? "Pick or search…"}
    />
  )
}

function ToggleSwitch({
  checked, disabled, onChange,
}: { checked: boolean; disabled?: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition",
        checked ? "bg-brand-red" : "bg-white/10",
        disabled && "opacity-40 cursor-not-allowed",
      )}
    >
      <span
        className={cn(
          "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition",
          checked ? "translate-x-5" : "translate-x-1",
        )}
      />
    </button>
  )
}
